import LineChart from '@bumble/app/components/LineChart';
import { Loading } from '@bumble/app/components/Loading';
import NoData from '@bumble/app/components/NoData';
import TimeRange from '@bumble/app/components/TimeRange';
import { TIME_BUTTONS } from '@bumble/app/constants';
import { useApi } from '@bumble/app/hooks/use-api';
import { COLORS } from '@bumble/app/theme';
import { Box, Container, Grid, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { DateTime, Duration, DurationLikeObject } from 'luxon';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';

function convertData(originalData: any) {
  const newData = [];

  for (const item of originalData) {
    const key = Object.keys(item)[0];
    const metric = key.split('.').pop() || '';
    let formattedLabel = '';
    if (metric === 'cpu') formattedLabel = 'CPU utilization (%)';
    if (metric === 'memory') formattedLabel = 'Memory usage (%)';
    if (metric === 'tx') formattedLabel = 'Network out (bytes/sec)';
    if (metric === 'rx') formattedLabel = 'Network in (bytes/sec)';
    const dataPoints = item[key].map((entry: any) => {
      let val = 0;
      if (metric === 'cpu') val = entry.cpu_p;
      if (metric === 'memory') val = entry['mem.usage'];
      if (metric === 'tx') val = entry['tx.bytes'];
      if (metric === 'rx') val = entry['rx.bytes'];
      return {
        time: Date.parse(entry['@timestamp']),
        value: val,
      };
    });

    newData.push({
      label: formattedLabel,
      data: dataPoints,
      percentage: metric === 'cpu' || metric === 'memory'
    });
  }

  return newData;
}

const containerStyle = { border: `4px solid ${COLORS.BACKGROUND}`, borderRadius: '8px' };

interface NodeMetricsProps {
  id: string;
}

const NodeMetrics = ({ id }: NodeMetricsProps) => {
  const api = useApi();

  const [timeRange, setTimeRange] = useState<DurationLikeObject>(TIME_BUTTONS[0].value);

  const { isLoading, data, refetch, isRefetching } = useQuery(
    ['nodeMetrics'],
    api.getNodeMetricsApiV1GetNodeMetricsGet.bind(null, {
      id,
      start_time: DateTime.now().minus(timeRange).toISO(),
      end_time: DateTime.now().toISO(),
    }),
    { enabled: true, cacheTime: 0 },
  );

  const handleTimeChange = (e: React.MouseEvent<HTMLElement>, val: DurationLikeObject) => {
    if (val) setTimeRange(val);
  };

  const handleRefreshClick = () => {
    refetch();
    toast.success('Refreshed chart data.');
  };

  useEffect(() => {
    refetch();
  }, [timeRange, refetch]);

  if (isLoading || isRefetching) {
    return <Loading />;
  }

  if (id === undefined || !data || data?.data.length === 0 || !Array.isArray(data?.data)) {
    return <NoData />;
  }
  const series = convertData(data.data);
  const selectedHours = parseInt(Duration.fromObject(timeRange).toFormat('h'), 10);
  return (
    <Box sx={{ py: 2 }}>
      <TimeRange
        timeValues={TIME_BUTTONS}
        selectedTime={timeRange}
        onTimeChange={handleTimeChange}
        onRefresh={handleRefreshClick}
      />
      <Container>
        <Grid container spacing={5} sx={{ marginTop: 1 }}>
          {series.map(({ label: seriesLabel, data: seriesData, percentage }) => (
            <Grid item xs={12} md={6} key={seriesLabel}>
              <Box sx={containerStyle}>
                <Typography variant="h5" sx={{ my: 2, textAlign: 'center' }}>
                  {seriesLabel}
                </Typography>
                <LineChart
                  data={seriesData}
                  loading={false}
                  label={seriesLabel}
                  hours={selectedHours}
                  percentage={percentage}
                />
              </Box>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default NodeMetrics;
